import { screenSize } from "../../theme";
import { useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components/macro";
import { XIcon } from "../Icons/Icons";
import { TextSmRegular } from "../Typography/TypographyV2";

interface IFlyoutProps {
  show: boolean;
  children: any;
  closeFlyout: () => void;
  title: string;
  subtitle?: string | (() => JSX.Element);
}

const SlideOutFade = keyframes`
 0% { opacity: 0; }
 100% { opacity: 1; }
`;
const Slide = keyframes`
 0% { right: -576px; }
 100% { right: 0; }
`;

const SlideOutOverlay = styled.div`
  position: fixed;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-end;
  align-items: stretch;
  align-content: stretch;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.overlayColor};
  animation-name: ${SlideOutFade};
  animation-duration: 0.3s;
  z-index: 900;
  overflow: hidden;
`;

const SlideOutContainer = styled.div`
  width: 576px;
  background: ${({ theme }) => theme.primaryBG};
  position: relative;
  flex: 0 0 auto;
  margin: ${({ theme }) => theme.resolve("spacing_none")};
  padding: ${({ theme }) => theme.resolve("spacing_none")};
  border-radius: ${({ theme }) => theme.resolve("radius_xxs")};
  font-family: ${({ theme }) => theme.fontFamily};
  overflow: auto;
  max-height: 100%;
  animation-name: ${Slide};
  animation-duration: 0.3s;
  display: flex;
  flex-direction: column;
  @media ${screenSize.small} {
    width: 100%;
  }
  .__react_component_tooltip.place-top::before {
    bottom: -1px;
    height: 8px !important;
  }
`;

const SlideOutHeader = styled.div`
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  padding: ${({ theme }) => theme.resolve("spacing_2xl")}
    ${({ theme }) => theme.resolve("spacing_4xl")};
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const SlideOutTitle = styled.h3`
  flex: 1;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_xl")};
  line-height: ${({ theme }) => theme.resolve("line_height_xl")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  margin: 0;
`;

const SlideOutBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")}
    ${({ theme }) => theme.resolve("spacing_4xl")}
    ${({ theme }) => theme.resolve("spacing_6xl")}
    ${({ theme }) => theme.resolve("spacing_4xl")};
`;

const CloseButtonIcon = styled.button`
  background: ${({ theme }) => theme.resolve("color_buttonIcon_bg_enabled")};
  border: ${({ theme }) => theme.resolve("border_none")} solid
    ${({ theme }) => theme.resolve("color_buttonIcon_border_enabled")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: ${({ theme }) => theme.resolve("radius_buttonIcon_standard")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.resolve("color_buttonIcon_bg_hover")};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const SlideOutPanel = ({ ...props }: IFlyoutProps) => {
  const { title, subtitle } = props;
  const escFunction = useCallback((event) => {
    if (event.keyCode === 27) {
      props.closeFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener("keyup", escFunction);

    return () => {
      document.removeEventListener("keyup", escFunction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ReactDOM.createPortal(
    <SlideOutOverlay data-testid="slideout-overlay">
      <SlideOutContainer>
        <SlideOutHeader>
          <div style={{ flex: 1 }}>
            <SlideOutTitle>{title}</SlideOutTitle>
            {subtitle &&
              (typeof subtitle === "string" ? (
                <TextSmRegular>{subtitle}</TextSmRegular>
              ) : (
                subtitle()
              ))}
          </div>

          <CloseButtonIcon onClick={props.closeFlyout} type="button">
            <XIcon />
          </CloseButtonIcon>
        </SlideOutHeader>
        <SlideOutBody>{props.children}</SlideOutBody>
      </SlideOutContainer>
    </SlideOutOverlay>,
    document.body
  );
};

export const SlideOut = ({ ...props }: IFlyoutProps) => {
  const { title, subtitle } = props;
  if (props.show) {
    return (
      <SlideOutPanel
        show={props.show}
        closeFlyout={props.closeFlyout}
        title={title}
        subtitle={subtitle}
      >
        {props.children}
      </SlideOutPanel>
    );
  }
  return null;
};
